import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import {
  listReviews,
  runReviewNow,
  decideCandidates,
  type StruckOffReview,
} from "../../../service/struck-off-review"

// The review step: the sweep found these names, someone decides each one.
//
// Nothing here happens on its own. Confirming re-checks the rule server-side
// first, so a parent who paid between the 11th and today means that child is
// skipped rather than struck off on a week-old list.
const StruckOffReviewModal = ({
  branchId,
  onClose,
  onChanged,
}: {
  branchId?: string
  onClose: () => void
  onChanged?: () => void
}) => {
  const [reviews, setReviews] = useState<StruckOffReview[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [picked, setPicked] = useState<Record<string, boolean>>({})

  const load = async () => {
    setLoading(true)
    try {
      const r = await listReviews({ branchId, status: "pending" })
      setReviews(r?.data || [])
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not load the review")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [branchId]) // eslint-disable-line react-hooks/exhaustive-deps

  const pending = reviews.flatMap((r) =>
    r.candidates.filter((c) => c.decision === "pending").map((c) => ({ ...c, reviewId: r._id }))
  )
  const chosen = pending.filter((c) => picked[c.student])

  const decide = async (decision: "confirmed" | "dismissed") => {
    if (!chosen.length) return
    setBusy(true)
    try {
      // Grouped per review because each carries its own sweep date.
      const byReview: Record<string, string[]> = {}
      for (const c of chosen) {
        if (!byReview[c.reviewId]) byReview[c.reviewId] = []
        byReview[c.reviewId].push(c.student)
      }
      let struck = 0
      let dismissed = 0
      let skipped = 0
      for (const [reviewId, studentIds] of Object.entries(byReview)) {
        const res = await decideCandidates(reviewId, { studentIds, decision })
        struck += res.changed
        dismissed += res.dismissed
        skipped += res.skipped
      }
      toast.success(
        decision === "confirmed"
          ? `${struck} struck off${skipped ? `, ${skipped} skipped — no longer meet the rule` : ""}`
          : `${dismissed} dismissed`
      )
      setPicked({})
      await load()
      onChanged?.()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not apply that")
    } finally {
      setBusy(false)
    }
  }

  const runNow = async () => {
    setBusy(true)
    try {
      await runReviewNow(branchId)
      toast.success("Rules run — the list below is up to date")
      await load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not run the rules")
    } finally {
      setBusy(false)
    }
  }

  const skipReasons = reviews.map((r) => r.absenceSkipReason).filter(Boolean)

  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title mb-0">Struck-off review</h5>
              <small className="text-muted">
                Nobody is taken off the rolls until you confirm them here
              </small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center text-muted py-4">Loading…</div>
            ) : (
              <>
                {skipReasons.length > 0 && (
                  <div className="alert alert-light border text-dark py-2 small">
                    {/* Why an empty absence list is empty. Without this the office
                        cannot tell "all is well" from "nobody is keeping the register". */}
                    {skipReasons.map((reason, i) => (
                      <div key={i}>{reason}</div>
                    ))}
                  </div>
                )}

                {pending.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    Nobody is currently flagged. The rules run on the 11th of each month.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={chosen.length === pending.length && pending.length > 0}
                              onChange={(e) =>
                                setPicked(
                                  e.target.checked
                                    ? Object.fromEntries(pending.map((c) => [c.student, true]))
                                    : {}
                                )
                              }
                            />
                          </th>
                          <th>GR</th>
                          <th>Name</th>
                          <th>Class</th>
                          <th>Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pending.map((c) => (
                          <tr key={c.student}>
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={!!picked[c.student]}
                                onChange={(e) =>
                                  setPicked((p) => ({ ...p, [c.student]: e.target.checked }))
                                }
                              />
                            </td>
                            <td>{c.grNumber}</td>
                            <td>{c.name}</td>
                            <td>{`${c.className || ""} ${c.sectionName || ""}`.trim() || "—"}</td>
                            <td>
                              <span
                                className={`badge me-2 ${
                                  c.rule === "unpaid" ? "badge-soft-danger" : "badge-soft-warning"
                                }`}
                              >
                                {c.rule === "unpaid" ? "Fees" : "Absence"}
                              </span>
                              {c.detail}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer d-flex justify-content-between">
            <button className="btn btn-outline-secondary" onClick={runNow} disabled={busy}>
              Run the rules now
            </button>
            <div className="d-flex gap-2">
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
              <button
                className="btn btn-outline-secondary"
                disabled={busy || !chosen.length}
                onClick={() => decide("dismissed")}
              >
                Dismiss {chosen.length ? `(${chosen.length})` : ""}
              </button>
              <button
                className="btn btn-danger"
                disabled={busy || !chosen.length}
                onClick={() => decide("confirmed")}
              >
                Strike off {chosen.length ? `(${chosen.length})` : ""}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StruckOffReviewModal
